import * as React from 'react';
import * as styles from "./AIAssistant.css";
import { IEnvironment } from '../../interfaces/IEnvironment';
import ContentOrganizerAIService, { ContentOrganizerAIResponse } from '../../services/ContentOrganizerAIService';
import { IAButton } from '../button/Button';
import { LocalizationService } from '../../services/LocalizationService';
import AIResponseCardSkeleton from './AIResponseCardSkeleton';
import AIResponseCard from './AIResponseCard';
import { Environment } from '../../services/Environment';
import { IFontSlots } from '../../interfaces/IFontSlots';
import { IAIcon } from '../icon/Icon';
import { IContentOrganizerCreate } from '../../interfaces/IContentOrganizerCreate';
import { IAEditableTitle, IEditableTitleChanges } from '../editableTitle/EditableTitle';

export interface IAIAssistantProps {
  environment: IEnvironment;
  tenant: string;
  highlightColor: string;
  sites: IContentOrganizerCreate[];
  contentLanguage: string;
  fontSlots?: IFontSlots;
  title?: string;
  showTitle?: boolean;
  infoText?: string;
  infoIconUrl?: string;
  infoHeadline?: string;
  example?: string;
  onClose?: () => void;
  isEditMode?: boolean;
  hasDarkBackground?: boolean;
  headLineColor?: string;
  headlineFontSize?: number;
  headlineFontStyle?: string;
  headlineTextAlign?: string;
  headlineFontWeight?: string;
  headlineTextDecoration?: string;
  headlineLineHeight?: number;
  headlineTag?: string;
  themeColors?: string[];
  onTitleSettingsChange?: (changes: IEditableTitleChanges) => void;
}

export interface IAIAssistantState {
  isLoadingAIResponse: boolean;
  query: string;
  aiResponse: ContentOrganizerAIResponse;
  isMobile: boolean;
}

export class AIAssistant extends React.Component<IAIAssistantProps, IAIAssistantState> {

  private localizationService = new LocalizationService();

  constructor(props: IAIAssistantProps) {
    super(props);
    this.state = {
      isLoadingAIResponse: undefined,
      query: "",
      aiResponse: undefined,
      isMobile: window.innerWidth <= 768
    };
    this.localizationService?.checkLocalizedStrings().then(() => this.forceUpdate());
  }

  componentDidMount(): void {
    document.getElementById('IA_AIAssistantSearchField').addEventListener('keypress', (event) => {
      if (event.key === "Enter") {
        event.preventDefault();
        this.getAIResponse();
      }
    });
  }

  private getAIResponse() {
    if (this.state.isLoadingAIResponse) {
      return;
    }
    this.setState({ isLoadingAIResponse: true, aiResponse: undefined }, () => {
      ContentOrganizerAIService.getAIResponse(
        this.props.environment,
        this.props.tenant,
        this.props.contentLanguage,
        this.props.sites.map(s => s.siteCollection),
        this.state.query)
        .then(response => {
          const hasContent = response.pageContents?.map(content => content.isIncludedInKnowledgebase).reduce((accumulator, current) => accumulator || current, false);
          if (!hasContent) {
            response.chatReply = this.localizationService.strings.AIAssistant_AiNoAnswerFound
          }
          this.setState({ aiResponse: response });
        })
        .catch(() => this.setState({
          aiResponse: {
            chatReply: this.localizationService.strings.AIAssistant_AiErrorMessage,
            pageContents: undefined,
            searchKeyWords: undefined,
            usage: undefined
          }
        }))
        .finally(() => this.setState({ isLoadingAIResponse: false }));
    });
  }

  render() {
    return (
      <>
        <div className={styles.IA_aiWrapper}>
          {(this.props.showTitle == undefined || this.props.showTitle) && this.props.title &&
            <div style={{ marginTop: -1, marginBottom: 3 }}>
              <IAEditableTitle
                show={true}
                permalinkId={"Ai Assistant"}
                isEditMode={this.props.isEditMode}
                title={this.props.title}
                headLineColor={this.props.headLineColor}
                hasDarkBackground={this.props.hasDarkBackground}
                headlineFontSize={this.props.headlineFontSize}
                headlineFontStyle={this.props.headlineFontStyle}
                headlineTextAlign={this.props.headlineTextAlign}
                headlineFontWeight={this.props.headlineFontWeight}
                headlineTextDecoration={this.props.headlineTextDecoration}
                headlineLineHeight={this.props.headlineLineHeight}
                headlineTag={this.props.headlineTag}
                themeColors={this.props.themeColors}
                fontFamily={this.props.fontSlots?.heading?.fontFamily}
                onChange={(changes) => this.props.onTitleSettingsChange?.(changes)}
              />
            </div>
          }
          {this.props.onClose &&
            <button
              aria-label="Close"
              style={{
                background: "none",
                border: "none",
                padding: 0,
                cursor: "pointer",
                position: "absolute",
                right: -20,
                top: 0
              }}
              onClick={() => this.props.onClose()}
            >
              <IAIcon
                title="Close"
                size={20}
                color={"gray"}
              />
            </button>
          }
          <div
            className={styles.IA_searchFieldWrapper}
            style={{
              marginTop: this.state.isMobile ? 11 : 20
            }}
          >
            <IAButton
              borderRadius={2}
              iconUrl="https://intraactivestorage.blob.core.windows.net/cdn/icons/Dismiss/SVG/ic_fluent_dismiss_48_regular.svg"
              disbaled={this.state.isLoadingAIResponse || !this.state.query}
              iconColor={this.props.hasDarkBackground ? "#b9b9b9" : "grey"}
              iconSize={12}
              style={{
                position: "relative",
                float: "right",
                marginRight: 15
              }}
              onClick={() => {
                this.setState({ query: "", aiResponse: undefined }, () => {
                  const searchField = document.getElementById("IA_AIAssistantSearchField");
                  if (searchField) {
                    searchField.innerHTML = "";
                  }
                });
              }}
            />
            <IAButton
              borderRadius={2}
              iconUrl="https://intraactivestorage.blob.core.windows.net/cdn/icons/Send/SVG/ic_fluent_send_28_filled.svg"
              disbaled={this.state.isLoadingAIResponse || !this.state.query}
              iconColor={this.props.hasDarkBackground ? "#ffffff" : this.props.highlightColor}
              iconSize={20}
              style={{
                position: "relative",
                float: "right",
                marginRight: 0
              }}
              onClick={() => this.getAIResponse()}
            />
            <div
              id="IA_AIAssistantSearchField"
              className={styles.IA_searchField}
              contentEditable={this.props.sites?.length > 0}
              placeholder={this.props.example && this.props.example !== "" ? this.props.example : this.localizationService.strings.AIAssistant_AiExampleFallback}
              onInput={(evt: any) => {
                if (evt && evt.target != null) {
                  this.setState({ query: evt.target.innerHTML });
                }
              }}
              style={{
                borderColor: this.state.isMobile ? (this.props.hasDarkBackground ? "#fff" : "#dddddd") : this.props.highlightColor,
                borderRadius: this.state.isMobile ? 5 : 10,
                userSelect: this.props.sites?.length > 0 ? "auto" : "none",
                opacity: this.props.sites?.length > 0 ? 1 : 0.5,
                backgroundColor: this.props.hasDarkBackground ? "transparent" : undefined,
                color: this.props.hasDarkBackground ? "#fff" : undefined
              }}
            />
          </div>
          {this.props.infoText && !this.state.isLoadingAIResponse && !this.state.aiResponse?.chatReply &&
            <div
              className={styles.IA_infoBox}
              style={{
                fontFamily: this.props.fontSlots?.body?.fontFamily
              }}
            >
              <div className={styles.IA_infoBoxIcon}>
                <IAIcon
                  url={this.props.infoIconUrl ? this.props.infoIconUrl : "https://intraactivestorage.blob.core.windows.net/cdn/icons/Info/SVG/ic_fluent_info_28_regular.svg"}
                  title="Info"
                  size={25}
                  color={this.props.highlightColor}
                />
              </div>
              <div className={styles.IA_infoBoxHeadline}>
                {this.props.infoHeadline != undefined && this.props.infoHeadline !== "" ? this.props.infoHeadline : this.localizationService.strings.AIAssistant_information}
              </div>
              <div className={styles.IA_infoBoxText}>
                {this.props.infoText}
              </div>
            </div>
          }
          {this.state.isLoadingAIResponse
            ?
            <AIResponseCardSkeleton />
            :
            <>
              {this.state.aiResponse?.chatReply &&
                <>
                  <AIResponseCard
                    hightlightColor={this.props.highlightColor}
                    reply={this.state.aiResponse?.chatReply}
                    fontSlots={this.props.fontSlots}
                    sources={this.state.aiResponse?.pageContents?.filter(content => content.isIncludedInKnowledgebase)?.map(content => {
                      const url = new URL(content.path);
                      let link = content.path;
                      let onClick: () => void;

                      // check if link is to a message to add link portal link
                      const messagePath = ".sharepoint.com/sites/IntraActive-Messages/Lists/";
                      const messagePathIndex = content.path?.indexOf(messagePath);
                      if (messagePathIndex > 0) {
                        const tenant = this.props.tenant.replace(".sharepoint.com", "");
                        const instanceInternalName = content.path.slice(messagePathIndex + messagePath.length, content.path.lastIndexOf("/DispForm.aspx"));
                        let instance: string;
                        if (instanceInternalName === "IAMessages") {
                          instance = "Default";
                        } else {
                          instance = decodeURI(instanceInternalName).replace(" Messages", "").trim();
                        }
                        const id = url.searchParams.get("ID");
                        link = `${Environment.getEnvironmentForLinkPortal(this.props.environment)}/${tenant}/${instance}/${id}`;

                        // when running inside the native mobile app, open the message in-app instead of the link portal
                        if ((window.platform === "ios" || window.platform === "android") && window.app?.showPushContent) {
                          onClick = () => window.app.showPushContent("message", instance, id);
                        }
                      }

                      return {
                        link,
                        title: content.title,
                        onClick
                      }
                    })}
                  />
                </>
              }
            </>
          }
        </div>
      </>
    );
  }
}
